import { isInventoryMeterMountType } from '@enmon/commons/lib/tools/api/inventory-meter';
import { findNextInventorizationRegisterPhotoRef } from '@/features/inventorization/domain/inventorization-registers';
import {
  type InventorizationTechnicalDetails,
  isRemoteReadingMountType,
  type MeterMountTypeSelection,
} from '@/features/inventorization/domain/inventorization-technical';
import { photoWizardSlotLabel, photoWizardText } from '@/features/inventorization/photos/photo-wizard-i18n';
import { type CaptureSession, type LocalPhotoRef, PHOTO_CONTEXTS } from '@/shared/capture/capture-session';
import {
  getInventorizationPhotoSlot,
  getInventorizationPhotosForSlot,
  INVENTORIZATION_PHOTO_SLOT_IDS,
  type PhotoSlotDefinition,
} from '@/shared/capture/inventorization-photo-slots';
import { AI_EXTRACTION_JOB_TYPES, AI_EXTRACTION_STATUSES } from '@/shared/ocr/ai-extraction';

const REQUIRED_PHOTOS_PER_SLOT = 1;

export const PHOTO_WIZARD_STEP_IDS = {
  accessibility: 'accessibility',
  mountType: 'mount-type',
} as const;

export const PHOTO_WIZARD_STEP_KINDS = {
  accessibility: 'accessibility',
  mountType: 'mount-type',
  photo: 'photo',
} as const;

export type WizardStep =
  | {
      id: typeof PHOTO_WIZARD_STEP_IDS.accessibility;
      kind: typeof PHOTO_WIZARD_STEP_KINDS.accessibility;
    }
  | {
      id: typeof PHOTO_WIZARD_STEP_IDS.mountType;
      kind: typeof PHOTO_WIZARD_STEP_KINDS.mountType;
    }
  | {
      id: PhotoSlotDefinition['id'];
      kind: typeof PHOTO_WIZARD_STEP_KINDS.photo;
      slot: PhotoSlotDefinition;
    };

const contextPhotoSlot = getInventorizationPhotoSlot(INVENTORIZATION_PHOTO_SLOT_IDS.context);
const detailPhotoSlot = getInventorizationPhotoSlot(INVENTORIZATION_PHOTO_SLOT_IDS.detail);
const registerPhotoSlot = getInventorizationPhotoSlot(INVENTORIZATION_PHOTO_SLOT_IDS.registerMain);
const modulePhotoSlot = getInventorizationPhotoSlot(INVENTORIZATION_PHOTO_SLOT_IDS.module);

export function getSlotPhotos(session: CaptureSession, slot: PhotoSlotDefinition) {
  return getInventorizationPhotosForSlot(session.photos, slot);
}

export function createCaptureSlot(slot: PhotoSlotDefinition, photos: LocalPhotoRef[]): PhotoSlotDefinition {
  if (slot.context !== PHOTO_CONTEXTS.register) {
    return slot;
  }

  const registerId = findNextInventorizationRegisterPhotoRef(photos);

  return {
    ...slot,
    id: `${slot.id}-${registerId}`,
    registerId,
  };
}

export function getWizardPhotoIds(session: CaptureSession, technical: InventorizationTechnicalDetails) {
  return createWizardSteps(technical).flatMap(step =>
    step.kind === PHOTO_WIZARD_STEP_KINDS.photo ? getSlotPhotos(session, step.slot).map(photo => photo.id) : [],
  );
}

export function hasCompletedLlmExtraction(session: CaptureSession) {
  return session.aiExtractions.some(
    extraction =>
      extraction.jobType === AI_EXTRACTION_JOB_TYPES.geminiExtraction &&
      (extraction.status === AI_EXTRACTION_STATUSES.queued ||
        extraction.status === AI_EXTRACTION_STATUSES.running ||
        extraction.status === AI_EXTRACTION_STATUSES.succeeded ||
        extraction.status === AI_EXTRACTION_STATUSES.skipped),
  );
}

export function hasCompletedInventorizePhotoWizard(session: CaptureSession) {
  if (session._id) {
    return true;
  }

  return hasCompletedLlmExtraction(session);
}

export function createWizardSteps(technical: InventorizationTechnicalDetails): WizardStep[] {
  return [
    { id: contextPhotoSlot.id, kind: PHOTO_WIZARD_STEP_KINDS.photo, slot: contextPhotoSlot },
    { id: PHOTO_WIZARD_STEP_IDS.accessibility, kind: PHOTO_WIZARD_STEP_KINDS.accessibility },
    { id: detailPhotoSlot.id, kind: PHOTO_WIZARD_STEP_KINDS.photo, slot: detailPhotoSlot },
    { id: registerPhotoSlot.id, kind: PHOTO_WIZARD_STEP_KINDS.photo, slot: registerPhotoSlot },
    { id: PHOTO_WIZARD_STEP_IDS.mountType, kind: PHOTO_WIZARD_STEP_KINDS.mountType },
    ...(isRemoteReadingMountType(technical.mountType)
      ? [{ id: modulePhotoSlot.id, kind: PHOTO_WIZARD_STEP_KINDS.photo, slot: modulePhotoSlot }]
      : []),
  ];
}

export function findFirstIncompleteStepIndex(session: CaptureSession, technical: InventorizationTechnicalDetails) {
  return createWizardSteps(technical).findIndex(step => !canContinueStep(step, session, technical));
}

export function hasCompletedRequiredWizardSteps(session: CaptureSession, technical: InventorizationTechnicalDetails) {
  return findFirstIncompleteStepIndex(session, technical) === -1;
}

export function canContinueStep(step: WizardStep, session: CaptureSession, technical: InventorizationTechnicalDetails) {
  if (step.kind === PHOTO_WIZARD_STEP_KINDS.accessibility) {
    return Boolean(technical.accessibility?.length);
  }

  if (step.kind === PHOTO_WIZARD_STEP_KINDS.mountType) {
    return isWizardMountType(technical.mountType);
  }

  if (step.slot.id === INVENTORIZATION_PHOTO_SLOT_IDS.module) {
    return getSlotPhotos(session, step.slot).length >= REQUIRED_PHOTOS_PER_SLOT;
  }

  return !step.slot.isRequired || getSlotPhotos(session, step.slot).length >= REQUIRED_PHOTOS_PER_SLOT;
}

export function getStepTitle(step: WizardStep) {
  if (step.kind === PHOTO_WIZARD_STEP_KINDS.accessibility) {
    return photoWizardText('steps.accessibility.title', 'Accessibility');
  }

  if (step.kind === PHOTO_WIZARD_STEP_KINDS.mountType) {
    return photoWizardText('steps.mountType.title', 'Remote-reading mount');
  }

  return photoWizardSlotLabel(step.slot);
}

export function getStepDescription(step: WizardStep) {
  if (step.kind === PHOTO_WIZARD_STEP_KINDS.accessibility) {
    return photoWizardText(
      'steps.accessibility.description',
      'Select every condition that affects physical access to this meter.',
    );
  }

  if (step.kind === PHOTO_WIZARD_STEP_KINDS.mountType) {
    return photoWizardText(
      'steps.mountType.description',
      'Confirm whether a remote-reading mount or module is installed.',
    );
  }

  if (step.slot.id === INVENTORIZATION_PHOTO_SLOT_IDS.module) {
    return photoWizardText(
      'steps.module.description',
      'Required for installed remote-reading mounts. Capture at least one module or interface photo.',
    );
  }

  return step.slot.isRequired
    ? photoWizardText(
        'steps.photo.requiredDescription',
        'Capture at least one photo. Empty tile examples will be added later.',
      )
    : photoWizardText('steps.photo.optionalDescription', 'Optional. Add photos if this category is present.');
}

function isWizardMountType(value: MeterMountTypeSelection) {
  return isInventoryMeterMountType(value);
}
